/**
 * Conceptos contables — el puente entre el negocio y el plan de cuentas.
 *
 * Aquí el contador define, UNA vez, que «Arriendo del local» va a la 512005 y
 * es gasto. Después el operario registra el egreso escogiendo lo que entiende
 * y la contabilidad sale bien sin que él sepa contabilidad.
 *
 * Si algo quedó mal clasificado se corrige el concepto, no los doce egresos.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Stack, Alert, Tooltip,
  Switch, FormControlLabel, CircularProgress, Tabs, Tab,
  ToggleButton, ToggleButtonGroup, Collapse, Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../api';
import { useEsMovil, FILA, ancho, tablaAncha, ENCABEZADO, botonEncabezado, rotulo } from '../estilos/responsivo';

const fmt = (v) => '$' + new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(Number(v || 0));

const VACIO = { nombre: '', descripcion: '', tipo: 'egreso', categoria: 'gasto',
                cuenta_contable_id: '', icono: '', orden: 0,
                requiere_revision: false, activo: true, vende: 'servicios' };

export default function Conceptos() {
  const esMovil = useEsMovil();
  const [tab, setTab] = useState(0);              // 0 egresos · 1 ingresos
  const [conceptos, setConceptos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [pendientes, setPendientes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [edicion, setEdicion] = useState(null);
  const [errorForm, setErrorForm] = useState('');
  const [avanzado, setAvanzado] = useState(false);   // opciones para el contador

  const tipo = tab === 0 ? 'egreso' : 'ingreso';

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [c, k, p] = await Promise.all([
        api.get('/conceptos', { params: { incluir_inactivos: true } }),
        api.get('/contabilidad/cuentas'),
        api.get('/conceptos/por-clasificar').catch(() => ({ data: null })),
      ]);
      setConceptos(c.data); setCuentas(k.data || []); setPendientes(p.data);
      setError('');
    } catch (e) { setError(e.response?.data?.detail || 'No se pudieron cargar los conceptos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!edicion.nombre?.trim()) { setErrorForm('Ponle un nombre al concepto.'); return; }
    // Un ingreso no necesita cuenta: con decir qué vende, el sistema le abre la suya.
    if (!edicion.cuenta_contable_id && edicion.tipo !== 'ingreso') {
      setErrorForm('Escoge la cuenta contable.'); return;
    }
    const cuerpo = { ...edicion,
      cuenta_contable_id: edicion.cuenta_contable_id ? parseInt(edicion.cuenta_contable_id, 10) : null,
      orden: parseInt(edicion.orden || 0, 10),
      categoria: edicion.tipo === 'egreso' ? edicion.categoria : null };
    try {
      if (edicion.id) await api.put(`/conceptos/${edicion.id}`, cuerpo);
      else await api.post('/conceptos', cuerpo);
      setEdicion(null); setErrorForm(''); cargar();
    } catch (e) { setErrorForm(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const borrar = async (c) => {
    if (!window.confirm(`¿Borrar el concepto «${c.nombre}»?`)) return;
    try { await api.delete(`/conceptos/${c.id}`); cargar(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo borrar'); }
  };

  // Un concepto de egreso apunta a 5/6/7; uno de ingreso, a 4. Ofrecer todo
  // el plan invita a equivocarse en el único campo que importa aquí.
  const cuentasValidas = cuentas.filter(c => {
    const d = String(c.codigo)[0];
    return edicion?.tipo === 'ingreso' ? d === '4' : ['5', '6', '7'].includes(d);
  });

  const visibles = conceptos.filter(c => c.tipo === tipo);

  return (
    <Box>
      <Box sx={ENCABEZADO}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {tipo === 'ingreso'
              ? 'Cómo agrupas lo que vendes: «Planes mensuales», «Cortes», «Repuestos»… La contabilidad sale sola.'
              : 'Lo que el usuario escoge en lenguaje de negocio, y a qué cuenta va. Se define una vez y todos los registros salen bien clasificados.'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          {...botonEncabezado(esMovil)}
          onClick={() => { setEdicion({ ...VACIO, tipo }); setErrorForm(''); setAvanzado(false); }}>
          {rotulo(esMovil, 'Nuevo', 'Nuevo concepto')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {pendientes?.cantidad > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          <b>{pendientes.cantidad} egreso{pendientes.cantidad !== 1 ? 's' : ''}</b> por
          {' '}{fmt(pendientes.monto)} entraron como «Otro gasto» y están sin clasificar.
          Vale la pena crearles su concepto y reasignarlos.
        </Alert>
      )}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab label="Egresos" />
          <Tab label="Ingresos" />
        </Tabs>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={tablaAncha(700)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Concepto', 'Va a la cuenta', tipo === 'ingreso' ? 'Vende' : 'Tipo', 'Usos', 'Estado', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                      textTransform: 'uppercase', color: '#64748b' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibles.map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                        {c.icono ? `${c.icono}  ` : ''}{c.nombre}
                      </Typography>
                      {c.descripcion && (
                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                          {c.descripcion}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, fontFamily: 'ui-monospace, monospace' }}>
                      {c.cuenta_codigo}
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary',
                                        fontFamily: 'system-ui' }}>
                        {c.cuenta_nombre}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {c.tipo === 'ingreso' && c.vende && (
                        <Chip size="small" label={c.vende === 'productos' ? 'Productos' : 'Servicios'}
                          sx={{ height: 20, fontSize: 10.5, bgcolor: '#ecfdf5', color: '#047857' }} />
                      )}
                      {c.categoria && (
                        <Chip size="small" label={c.categoria}
                          sx={{ height: 20, fontSize: 10.5,
                                bgcolor: c.categoria === 'costo' ? '#fef3c7' : '#e0f2fe',
                                color: c.categoria === 'costo' ? '#a16207' : '#0369a1' }} />
                      )}
                      {c.requiere_revision && (
                        <Tooltip title="Lo que entre por aquí queda marcado para que contabilidad lo reclasifique">
                          <Chip size="small" label="por clasificar"
                            sx={{ height: 20, fontSize: 10.5, ml: 0.5,
                                  bgcolor: '#fee2e2', color: '#b91c1c' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{c.usos}</TableCell>
                    <TableCell>
                      <Chip size="small" label={c.activo ? 'Activo' : 'Inactivo'}
                        color={c.activo ? 'success' : 'default'}
                        variant={c.activo ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small"
                          onClick={() => { setEdicion({ ...c }); setErrorForm(''); setAvanzado(false); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Borrar">
                        <IconButton size="small" color="error" onClick={() => borrar(c)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {visibles.length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 5,
                    color: 'text.disabled' }}>
                    No hay conceptos de {tipo}.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog fullScreen={esMovil} open={Boolean(edicion)} onClose={() => setEdicion(null)} fullWidth maxWidth="sm">
        <DialogTitle>{edicion?.id ? 'Editar concepto' : 'Nuevo concepto'}</DialogTitle>
        <DialogContent dividers>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}
          {edicion && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Icono" sx={{ width: 90 }}
                  value={edicion.icono || ''} helperText="Opcional"
                  onChange={e => setEdicion({ ...edicion, icono: e.target.value })} />
                <TextField size="small" label="Nombre *" fullWidth value={edicion.nombre}
                  helperText={edicion.tipo === 'ingreso'
                    ? 'Cómo agrupas lo que vendes: «Planes mensuales», «Cortes de cabello»'
                    : 'Como lo diría alguien del negocio: «Arriendo del local»'}
                  onChange={e => setEdicion({ ...edicion, nombre: e.target.value })} />
              </Stack>
              <TextField size="small" label="Descripción" fullWidth
                value={edicion.descripcion || ''}
                onChange={e => setEdicion({ ...edicion, descripcion: e.target.value })} />
              {edicion.tipo === 'ingreso' && !edicion.id && (
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>¿Qué vende?</Typography>
                  <ToggleButtonGroup exclusive size="small" color="primary" fullWidth
                    value={edicion.vende || 'servicios'}
                    onChange={(_, v) => v && setEdicion({ ...edicion, vende: v })}>
                    <ToggleButton value="servicios">Servicios</ToggleButton>
                    <ToggleButton value="productos">Productos</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
              {edicion.tipo === 'egreso' && (
              <Stack direction={FILA} spacing={2}>
                <TextField select size="small" label="Tipo" fullWidth value={edicion.tipo}
                  onChange={e => setEdicion({ ...edicion, tipo: e.target.value,
                    cuenta_contable_id: '' })}>
                  <MenuItem value="egreso">Egreso — algo que se paga</MenuItem>
                  <MenuItem value="ingreso">Ingreso — algo que se factura</MenuItem>
                </TextField>
                {edicion.tipo === 'egreso' && (
                  <TextField select size="small" label="Categoría" fullWidth
                    value={edicion.categoria || 'gasto'}
                    helperText="El usuario ya no tendrá que decidirlo"
                    onChange={e => setEdicion({ ...edicion, categoria: e.target.value })}>
                    <MenuItem value="costo">Costo — va al servicio</MenuItem>
                    <MenuItem value="gasto">Gasto — de administración</MenuItem>
                  </TextField>
                )}
              </Stack>
              )}
              {edicion.tipo === 'ingreso' && (
                <Box>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {edicion.id
                      ? <>Va a la cuenta <b>{edicion.cuenta_codigo} {edicion.cuenta_nombre}</b>.</>
                      : 'No necesitas saber de contabilidad: el sistema le abre su propia cuenta de ingreso.'}
                    {' '}
                    <Link component="button" type="button" sx={{ fontSize: 12.5 }}
                      onClick={() => setAvanzado(!avanzado)}>
                      {avanzado ? 'Ocultar opciones para el contador' : 'Opciones para el contador'}
                    </Link>
                  </Typography>
                </Box>
              )}
              <Collapse in={edicion.tipo !== 'ingreso' || avanzado} unmountOnExit>
              <TextField select size="small" label={edicion.tipo === 'ingreso' ? 'Cuenta contable' : 'Cuenta contable *'} fullWidth
                value={edicion.cuenta_contable_id || ''}
                helperText={edicion.tipo === 'ingreso'
                  ? (edicion.id ? 'Solo cuentas de ingreso (4xxx)' : 'Vacío = el sistema crea una cuenta propia para este concepto')
                  : 'Solo cuentas de gasto (5xxx) o costo (6xxx / 7xxx)'}
                onChange={e => setEdicion({ ...edicion, cuenta_contable_id: e.target.value })}>
                {edicion.tipo === 'ingreso' && !edicion.id && (
                  <MenuItem value="">Crear una cuenta propia (recomendado)</MenuItem>
                )}
                {cuentasValidas.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.nombre}</MenuItem>
                ))}
              </TextField>
              </Collapse>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField size="small" label="Orden" type="number" sx={ancho(110)}
                  value={edicion.orden ?? 0}
                  onChange={e => setEdicion({ ...edicion, orden: e.target.value })} />
                <FormControlLabel control={
                  <Switch checked={Boolean(edicion.activo)}
                    onChange={e => setEdicion({ ...edicion, activo: e.target.checked })} />
                } label="Activo" />
              </Stack>
              {edicion.tipo === 'egreso' && (
                <FormControlLabel control={
                  <Switch checked={Boolean(edicion.requiere_revision)}
                    onChange={e => setEdicion({ ...edicion, requiere_revision: e.target.checked })} />
                } label="Lo que entre por aquí queda marcado para reclasificar" />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEdicion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
